import { Component, Inject, signal, ViewChild } from '@angular/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbBottomSheetComponent } from '@components/abb-bottom-sheet/abb-bottom-sheet.component';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import {
  BOTTOM_SHEET_DATA,
  BottomSheetData,
} from '@components/abb-bottom-sheet/bottom-sheet';
import { SelectionModel } from '@angular/cdk/collections';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SELECTION } from '@utils/table';

type SelectionBarData<T> = {
  selection?: SelectionModel<T>;
  actions: MenuItem<T>[];
};

@Component({
  selector: 'abb-selection-bar',
  standalone: true,
  template: `
    <abb-bottom-sheet #selectionBarRef class="absolute bottom-0 right-10">
      <div
        class="w-full flex flex-row justify-between text-abb-font my-auto mx-8"
      >
        <span class="content-center">
          {{
            '{count} items selected'
              | t : { count: data.selection?.selected?.length }
          }}
        </span>
        <div class="flex flex-row gap-5">
          @if(data.actions){ @for(action of ( data.selection?.selected!.length >
          1 ? multipleSelectionActions() : singleSelectionActions()); track
          action;){
          <span
            class="line-clamp-3 max-w-full text-abb-font"
            [matTooltip]="action.label | t"
          >
            <abb-button
              type="button"
              variant="none"
              [size]="'36'"
              class="text-abb-primary hover:drop-shadow-lg active:text-abb-primary-dark hover:text-abb-primary-dark transition-all items-center"
              (click)="actionClick(action)"
              [icon]="action?.icon"
            />
          </span>
          } }
        </div>
      </div>
    </abb-bottom-sheet>
  `,
  host: {
    class:
      'relative h-16 w-[var(--internalPageWidth)] -mr-10 bg-abb-black/[0.02]',
  },
  styles: ``,
  imports: [
    TPipe,
    AbbBottomSheetComponent,
    AbbButtonComponent,
    MatTooltipModule,
  ],
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'commons' }],
})
export class SelectionBarComponent<T>
  implements BottomSheetData<SelectionBarData<T>>
{
  @ViewChild('selectionBarRef') selectionBarRef:
    | AbbBottomSheetComponent
    | undefined;
  singleSelectionActions = signal<MenuItem<T>[]>([]);
  multipleSelectionActions = signal<MenuItem<T>[]>([]);
  constructor(@Inject(BOTTOM_SHEET_DATA) public data: SelectionBarData<T>) {
    this.singleSelectionActions.set(
      this.data.actions.filter((a) =>
        [SELECTION.ALWAYS, SELECTION.SINGLE].includes(a.selectionAllowed!)
      )
    );
    this.multipleSelectionActions.set(
      this.data.actions.filter((a) =>
        [SELECTION.ALWAYS, SELECTION.MULTIPLE].includes(a.selectionAllowed!)
      )
    );
    this.data.selection?.changed.subscribe((s) => {
      if (s.source.selected?.length === 0) {
        this.close();
      }
    });
  }

  actionClick(action: MenuItem<T>) {
    action.action(this.data.selection!.selected)?.then(() => {
      this.data.selection?.clear();
    });
  }

  close() {
    this.selectionBarRef?.dispose();
  }
}
